(() => {
  const aurenText = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), window.aurenStrings?.[key] || key);
  const aurenOption = value => window.aurenOptionLabels?.[value] || value;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;

  if (!motionPreference.matches) {
    document.documentElement.classList.add('auren-motion-ready');
  }

  const initReveal = (root = document) => {
    const elements = root.querySelectorAll('.auren-reveal:not([data-auren-reveal-ready])');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window) || motionPreference.matches) {
      elements.forEach((element) => {
        element.dataset.aurenRevealReady = 'true';
        element.classList.add('is-visible');
      });
      return;
    }

    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    elements.forEach((element) => {
      element.dataset.aurenRevealReady = 'true';
      revealObserver.observe(element);
    });
  };

  const initComparisons = (root = document) => {
    root.querySelectorAll('[data-auren-comparison]:not([data-auren-ready])').forEach((section) => {
      section.dataset.aurenReady = 'true';
      const stage = section.querySelector('.auren-comparison__stage');
      const before = section.querySelector('.auren-comparison__before');
      const range = section.querySelector('.auren-comparison__range');
      if (!stage || !before || !range) return;

      const update = () => {
        const percentage = `${range.value}%`;
        stage.style.setProperty('--auren-position', percentage);
        before.style.setProperty('--auren-stage-width', `${stage.clientWidth}px`);
        range.setAttribute('aria-valuetext', `${range.value}% avant`);
      };

      range.addEventListener('input', update);
      const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
      if (observer) observer.observe(stage);
      else window.addEventListener('resize', update, { passive: true });
      section.aurenComparisonCleanup = () => {
        observer?.disconnect();
        window.removeEventListener('resize', update);
        range.removeEventListener('input', update);
        delete section.dataset.aurenReady;
      };
      update();
    });
  };

  const normalizeMediaLabel = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

  const isUniversalMedia = (label) => ['always_display', 'always display', 'all', 'toutes'].includes(normalizeMediaLabel(label));

  const mediaMatchesColor = (label, color) => {
    const normalizedLabel = normalizeMediaLabel(label);
    const normalizedColor = normalizeMediaLabel(color);
    if (!normalizedLabel || !normalizedColor) return false;
    if (normalizedLabel === normalizedColor) return true;
    return [
      `[${normalizedColor}]`,
      `${normalizedColor} |`,
      `${normalizedColor} -`,
      `${normalizedColor} –`,
      `${normalizedColor} —`,
      `${normalizedColor}:`,
      `${normalizedColor} /`,
      `${normalizedColor}_`,
    ].some((prefix) => normalizedLabel.startsWith(prefix));
  };

  const resetGallerySlider = (slider, visibleCount) => {
    slider?.resetPages?.();
    // Dawn skips pagination updates when a colour has only one image.
    if (!slider || visibleCount > 1) return;
    slider.currentPage = 1;
    slider.totalPages = 1;
    slider.slidesPerPage = 1;
    slider.sliderItemOffset = slider.slider?.clientWidth || 1;
    slider.prevButton?.setAttribute('disabled', 'disabled');
    slider.nextButton?.setAttribute('disabled', 'disabled');
    if (slider.currentPageElement) slider.currentPageElement.textContent = '1';
    if (slider.pageTotalElement) slider.pageTotalElement.textContent = '1';
  };

  const filterGalleryByColor = (gallery, color, activateFirst = true) => {
    const slides = [...gallery.querySelectorAll('.product__media-item[data-auren-media-alt]')];
    if (!slides.length || !color) return;

    const colorSlides = slides.filter((slide) => mediaMatchesColor(slide.dataset.aurenMediaAlt, color));
    const canFilter = colorSlides.length > 0;
    const visibleSlides = [];

    slides.forEach((slide) => {
      const visible = !canFilter || mediaMatchesColor(slide.dataset.aurenMediaAlt, color) || isUniversalMedia(slide.dataset.aurenMediaAlt);
      slide.classList.toggle('auren-media-filtered', !visible);
      if (visible) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      if (visible) visibleSlides.push(slide);
    });

    gallery.querySelectorAll('.thumbnail-list__item[data-auren-media-alt]').forEach((thumbnail) => {
      const visible = !canFilter || mediaMatchesColor(thumbnail.dataset.aurenMediaAlt, color) || isUniversalMedia(thumbnail.dataset.aurenMediaAlt);
      thumbnail.classList.toggle('auren-media-filtered', !visible);
      if (visible) thumbnail.removeAttribute('aria-hidden');
      else thumbnail.setAttribute('aria-hidden', 'true');
    });

    gallery.dataset.aurenFilterValue = color;
    const colorStatus = gallery.querySelector('[data-auren-gallery-status]');
    const colorStatusLabel = gallery.querySelector('[data-auren-gallery-color]');
    if (colorStatusLabel) colorStatusLabel.textContent = aurenOption(color);
    if (colorStatus) {
      colorStatus.classList.remove('is-changing');
      window.requestAnimationFrame(() => colorStatus.classList.add('is-changing'));
    }
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    resetGallerySlider(viewer, visibleSlides.length);
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails) {
      const visibleThumbnails = thumbnails.querySelectorAll('.thumbnail-list__item:not(.auren-media-filtered)');
      resetGallerySlider(thumbnails, visibleThumbnails.length);
    }

    const currentCounter = gallery.querySelector('.slider-counter--current');
    const totalCounter = gallery.querySelector('.slider-counter--total');
    if (currentCounter) currentCounter.textContent = '1';
    if (totalCounter) totalCounter.textContent = String(visibleSlides.length);

    if (!activateFirst || !visibleSlides.length) return;
    const firstColorSlide = colorSlides[0] || visibleSlides[0];
    const mediaId = firstColorSlide?.dataset.mediaId;
    if (mediaId && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false, { scrollToMedia: false });
    } else if (firstColorSlide) {
      slides.forEach((slide) => slide.classList.toggle('is-active', slide === firstColorSlide));
      firstColorSlide.parentElement?.scrollTo?.({ left: firstColorSlide.offsetLeft, behavior: motionPreference.matches ? 'instant' : 'smooth' });
    }
  };

  const initColorGalleries = (root = document) => {
    root.querySelectorAll('media-gallery[data-auren-filter-option]:not([data-auren-filter-ready])').forEach((gallery) => {
      gallery.dataset.aurenFilterReady = 'true';
      filterGalleryByColor(gallery, gallery.dataset.aurenFilterValue, true);

      const mediaList = gallery.querySelector('.product__media-list');
      if (mediaList && 'MutationObserver' in window) {
        const observer = new MutationObserver(() => {
          window.cancelAnimationFrame(gallery.aurenMediaFrame);
          gallery.aurenMediaFrame = window.requestAnimationFrame(() => {
            if (gallery.isConnected) filterGalleryByColor(gallery, gallery.dataset.aurenFilterValue, true);
          });
        });
        observer.observe(mediaList, { childList: true });
        gallery.aurenMediaObserver = observer;
      }
    });
  };

  const initProductInformation = (root = document) => {
    root.querySelectorAll('[data-auren-product-info]:not([data-auren-info-ready])').forEach((component) => {
      component.dataset.aurenInfoReady = 'true';
      const details = [...component.querySelectorAll('.auren-info')];
      details.forEach((item) => item.addEventListener('toggle', () => {
        if (!item.open) return;
        details.forEach((other) => {
          if (other !== item) other.open = false;
        });
      }));
    });
  };

  const init = (root = document) => {
    initReveal(root);
    initComparisons(root);
    initColorGalleries(root);
    initProductInformation(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.auren-reveal').forEach((element) => {
      revealObserver?.unobserve(element);
      delete element.dataset.aurenRevealReady;
    });
    event.target.querySelectorAll('[data-auren-comparison]').forEach((section) => section.aurenComparisonCleanup?.());
    event.target.querySelectorAll('media-gallery[data-auren-filter-ready]').forEach((gallery) => {
      gallery.aurenMediaObserver?.disconnect();
      window.cancelAnimationFrame(gallery.aurenMediaFrame);
      delete gallery.dataset.aurenFilterReady;
    });
  });

  document.addEventListener('focusin', (event) => {
    let section = event.target.closest?.('.auren-reveal');
    while (section) {
      section.classList.add('is-visible');
      revealObserver?.unobserve(section);
      section = section.parentElement?.closest('.auren-reveal');
    }
  });

  motionPreference.addEventListener?.('change', ({ matches }) => {
    document.documentElement.classList.toggle('auren-motion-ready', !matches);
    if (!matches) return;
    revealObserver?.disconnect();
    document.querySelectorAll('.auren-reveal').forEach((element) => element.classList.add('is-visible'));
  });

  document.addEventListener('change', (event) => {
    const selector = event.target.closest?.('variant-selects');
    if (!selector) return;
    const selectedControl = event.target.matches('select') ? event.target.selectedOptions?.[0] : event.target;
    const optionPosition = selectedControl?.dataset.optionPosition;
    if (!optionPosition) return;

    const galleryRoot = selector.closest('product-info') || document;
    galleryRoot.querySelectorAll(`media-gallery[data-auren-filter-option="option${optionPosition}"]`).forEach((gallery) => {
      filterGalleryByColor(gallery, event.target.value, true);
    });
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.getElementById(`MainProduct-${data?.sectionId}`);
      if (!productInfo || !data?.variant) return;
      productInfo.querySelectorAll('media-gallery[data-auren-filter-option]').forEach((gallery) => {
        const optionKey = gallery.dataset.aurenFilterOption;
        filterGalleryByColor(gallery, data.variant[optionKey], true);
      });
    });
  }

  if (!customElements.get('auren-bundle')) {
    customElements.define('auren-bundle', class AurenBundle extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bundleReady === 'true') {
          this.mountStickyBar();
          this.initStickyBar();
          return;
        }
        this.dataset.bundleReady = 'true';

        this.plans = [...this.querySelectorAll('[data-bundle-plan]')];
        this.items = [...this.querySelectorAll('[data-bundle-item]')];
        this.variants = this.readVariants();
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.total = this.querySelector('[data-bundle-total]');
        this.error = this.querySelector('[data-bundle-error]');
        this.sticky = this.querySelector('[data-bundle-sticky]');
        this.stickyLabel = this.querySelector('[data-bundle-sticky-label]');
        this.stickyPrice = this.querySelector('[data-bundle-sticky-price]');
        this.stickySubmit = this.querySelector('[data-bundle-sticky-submit]');
        this.submitLabel = this.querySelector('[data-bundle-submit-label]');
        this.idleSubmitLabel = this.submitLabel?.textContent || aurenText('add');

        this.plans.forEach((plan) => plan.addEventListener('change', () => this.update()));
        this.items.forEach((item) => {
          item.querySelectorAll('[data-bundle-option-value]').forEach((input) => {
            input.addEventListener('change', (event) => {
              const changedPosition = Number(event.currentTarget.dataset.optionPosition || 0);
              const variant = this.resolveItemVariant(item, changedPosition);
              if (variant) variant.options.forEach((value, index) => this.syncGalleryColor(index + 1, value));
              this.update();
            });
            // A checked colour can belong to another body than the one currently pictured.
            input.addEventListener('click', () => {
              if (input.checked && !input.disabled) this.syncGalleryColor(Number(input.dataset.optionPosition || 0), input.value);
            });
          });
          this.resolveItemVariant(item);
        });
        this.submitButton?.addEventListener('click', () => this.addBundle());
        this.stickySubmit?.addEventListener('click', () => this.addBundle());
        this.addEventListener('auren:upsell-change', () => this.update());
        this.querySelector('[data-bundle-sticky-edit]')?.addEventListener('click', () => {
          const target = this.querySelector('.auren-bundle__plans');
          target?.scrollIntoView({ behavior: motionPreference.matches ? 'instant' : 'smooth', block: 'center' });
          this.activePlan?.focus({ preventScroll: true });
        });
        this.mountStickyBar();
        this.initStickyBar();
        this.update();
      }

      readVariants() {
        try {
          return JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '[]');
        } catch (_error) {
          return [];
        }
      }

      syncGalleryColor(optionPosition, value) {
        if (!optionPosition || !value) return;
        const galleryRoot = this.closest('product-info') || document;
        galleryRoot.querySelectorAll(`media-gallery[data-auren-filter-option="option${optionPosition}"]`).forEach((gallery) => {
          if (gallery.dataset.aurenFilterValue !== value) filterGalleryByColor(gallery, value, true);
        });
      }

      get activePlan() {
        return this.plans.find((plan) => plan.checked) || this.plans[0];
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
            style: 'currency',
            currency: this.dataset.currency || 'EUR',
          }).format(cents / 100);
        } catch (_error) {
          return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
        }
      }

      getItemOptions(item) {
        return [...item.querySelectorAll('[data-bundle-option]')]
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((fieldset) => fieldset.querySelector('[data-bundle-option-value]:checked')?.value || '');
      }

      setItemOptions(item, options) {
        options.forEach((value, index) => {
          const input = [...item.querySelectorAll(`[data-bundle-option][data-option-position="${index + 1}"] [data-bundle-option-value]`)]
            .find((candidate) => candidate.value === value);
          if (input) input.checked = true;
        });
      }

      updateOptionAvailability(item) {
        const selected = this.getItemOptions(item);
        item.querySelectorAll('[data-bundle-option-value]').forEach((input) => {
          const position = Number(input.dataset.optionPosition || 1) - 1;
          const combinationExists = this.variants.some((variant) => variant.available && variant.options.every((value, index) => (
            index === position ? value === input.value : index > position || !selected[index] || value === selected[index]
          )));
          input.disabled = !combinationExists;
          input.nextElementSibling?.classList.toggle('is-unavailable', !combinationExists);
        });
      }

      resolveItemVariant(item, changedPosition = 0) {
        if (!this.variants.length) return null;
        if (!item.querySelector('[data-bundle-option]')) {
          const defaultVariant = this.variants.find((candidate) => candidate.available) || null;
          item.dataset.variantId = defaultVariant?.id || '';
          item.dataset.variantPrice = defaultVariant?.price || '0';
          item.dataset.variantComparePrice = defaultVariant?.comparePrice || defaultVariant?.price || '0';
          return defaultVariant;
        }
        let selected = this.getItemOptions(item);
        const originalOptions = [...selected];
        let variant = this.variants.find((candidate) => candidate.available && candidate.options.every((value, index) => value === selected[index]));

        if (!variant && changedPosition) {
          variant = this.variants.find((candidate) => candidate.available && candidate.options[changedPosition - 1] === selected[changedPosition - 1]);
          if (variant) {
            this.setItemOptions(item, variant.options);
            selected = variant.options;
          }
        }
        if (changedPosition) {
          const note = item.querySelector('[data-bundle-selection-note]');
          if (note) {
            // Announce only a real automatic change to another option (for example, a size).
            const otherOptionAdjusted = variant && originalOptions.some((value, index) => index !== changedPosition - 1 && value !== variant.options[index]);
            note.hidden = !otherOptionAdjusted;
            note.textContent = otherOptionAdjusted ? aurenText('adjusted', { title: variant.options.map(aurenOption).join(' / ') }) : '';
          }
        }

        item.dataset.variantId = variant?.id || '';
        item.dataset.variantPrice = variant?.price || '0';
        item.dataset.variantComparePrice = variant?.comparePrice || variant?.price || '0';
        item.classList.toggle('is-invalid', !variant);
        item.querySelector('[data-bundle-item-error]')?.toggleAttribute('hidden', Boolean(variant));

        item.querySelectorAll('[data-bundle-option]').forEach((fieldset) => {
          const selectedInput = fieldset.querySelector('[data-bundle-option-value]:checked');
          const valueLabel = fieldset.querySelector('[data-bundle-selected-value]');
          if (valueLabel && selectedInput) valueLabel.textContent = aurenOption(selectedInput.value);
        });

        const summary = item.querySelector('[data-bundle-item-summary]');
        if (summary) summary.textContent = (variant?.options || selected.filter(Boolean)).map(aurenOption).join(' / ');
        const image = item.querySelector('[data-bundle-image]');
        if (image && variant?.image) image.src = variant.image;
        this.updateOptionAvailability(item);
        return variant;
      }

      updatePlanCards(baseVariant) {
        if (!baseVariant) return;
        const unitPrice = Number(baseVariant.price || 0);
        const unitCompare = Math.max(Number(baseVariant.comparePrice || 0), unitPrice);

        this.plans.forEach((plan) => {
          const card = plan.closest('.auren-bundle__plan');
          const quantity = Number(plan.dataset.quantity || 1);
          const paidQuantity = Math.min(Number(plan.dataset.paidQuantity || quantity), quantity);
          const price = unitPrice * paidQuantity;
          const compare = unitCompare * quantity;
          card?.classList.toggle('is-selected', plan.checked);
          const priceElement = card?.querySelector('[data-plan-price]');
          const compareElement = card?.querySelector('[data-plan-compare]');
          const unitElement = card?.querySelector('[data-plan-unit-price]');
          if (priceElement) priceElement.textContent = this.formatMoney(price);
          if (unitElement) unitElement.textContent = `${this.formatMoney(price / quantity)} / ${aurenText('unit')}`;
          if (compareElement) {
            compareElement.textContent = this.formatMoney(compare);
            compareElement.hidden = compare <= price;
          }
        });
      }

      update() {
        const plan = this.activePlan;
        if (!plan) return;

        const quantity = Number(plan.dataset.quantity || 1);
        const paidQuantity = Math.min(Number(plan.dataset.paidQuantity || quantity), quantity);
        const activeItems = this.items.slice(0, quantity);
        this.items.forEach((item, index) => {
          item.hidden = index >= quantity;
          item.setAttribute('aria-hidden', String(index >= quantity));
        });

        const selectedVariants = activeItems.map((item) => this.resolveItemVariant(item)).filter(Boolean);
        const prices = selectedVariants.map((variant) => Number(variant.price || 0));
        const comparePrices = selectedVariants.map((variant, index) => Math.max(Number(variant.comparePrice || 0), prices[index]));
        const freeCount = Math.max(0, quantity - paidQuantity);
        const freeValue = [...prices].sort((a, b) => a - b).slice(0, freeCount).reduce((sum, price) => sum + price, 0);
        const bundleTotal = prices.reduce((sum, price) => sum + price, 0) - freeValue;
        const upsellTotal = (this.querySelector('auren-upsells')?.selectedItems?.() || []).reduce((sum, offer) => sum + offer.price, 0);
        const orderTotal = bundleTotal + upsellTotal;
        const upsellSummary = this.querySelector('[data-bundle-upsell-total]');
        if (upsellSummary) {
          upsellSummary.hidden = !upsellTotal;
          upsellSummary.textContent = `${window.aurenStrings?.extras || 'Articles complémentaires'} : + ${this.formatMoney(upsellTotal)}`;
        }
        const compareTotal = comparePrices.reduce((sum, price) => sum + price, 0);
        const isComplete = selectedVariants.length === quantity;

        this.updatePlanCards(selectedVariants[0] || this.variants.find((variant) => variant.available));
        if (isComplete) {
          const selectedCard = plan.closest('.auren-bundle__plan');
          const selectedPrice = selectedCard?.querySelector('[data-plan-price]');
          const selectedUnit = selectedCard?.querySelector('[data-plan-unit-price]');
          const selectedCompare = selectedCard?.querySelector('[data-plan-compare]');
          if (selectedPrice) selectedPrice.textContent = this.formatMoney(bundleTotal);
          if (selectedUnit) selectedUnit.textContent = `${this.formatMoney(bundleTotal / quantity)} / ${aurenText('unit')}`;
          if (selectedCompare) {
            selectedCompare.textContent = this.formatMoney(compareTotal);
            selectedCompare.hidden = compareTotal <= bundleTotal;
          }
        }
        const summaryLabel = this.querySelector('[data-bundle-summary-label]');
        this.querySelector('[data-bundle-summary]')?.removeAttribute('hidden');
        const summaryTotal = this.querySelector('[data-bundle-summary-total]');
        const summaryDetail = this.querySelector('[data-bundle-summary-detail]');
        if (summaryLabel) summaryLabel.textContent = quantity === 1 ? aurenText('body') : aurenText('pack', { count: quantity });
        if (summaryTotal) summaryTotal.textContent = isComplete ? this.formatMoney(bundleTotal) : aurenText('incomplete');
        if (summaryDetail) summaryDetail.textContent = isComplete
          ? aurenText('estimated', { price: this.formatMoney(bundleTotal / quantity) }) + (quantity > 1 ? aurenText('average') : '')
          : aurenText('choose');
        if (this.total) this.total.textContent = this.formatMoney(orderTotal);
        if (this.submitButton && !this.submitButton.classList.contains('is-loading')) {
          this.submitButton.disabled = !isComplete;
          this.stickySubmit?.toggleAttribute('disabled', !isComplete);
        }
        if (this.stickyLabel) this.stickyLabel.textContent = plan.dataset.label || this.dataset.productTitle;
        if (this.stickyPrice) this.stickyPrice.textContent = isComplete ? this.formatMoney(orderTotal) : aurenText('incomplete');
      }

      selectedItems() {
        const quantity = Number(this.activePlan?.dataset.quantity || 1);
        const counts = new Map();
        this.items.slice(0, quantity).forEach((item) => {
          const id = Number(item.dataset.variantId || 0);
          if (id) counts.set(id, (counts.get(id) || 0) + 1);
        });
        return [...counts.entries()].map(([id, quantityValue]) => ({ id, quantity: quantityValue }));
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.setAttribute('aria-busy', String(isLoading));
        if (this.submitLabel) this.submitLabel.textContent = isLoading ? aurenText('adding') : this.idleSubmitLabel;
        this.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = isLoading; });
        this.querySelectorAll('auren-upsells input,auren-upsells select').forEach(input => { input.disabled = isLoading; });
        this.stickySubmit?.toggleAttribute('disabled', isLoading);
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
        if (message) {
          this.error.focus({ preventScroll: true });
          this.error.scrollIntoView({ behavior: motionPreference.matches ? 'instant' : 'smooth', block: 'center' });
        }
      }

      async addBundle() {
        if (this.submitButton?.disabled) return;
        const items = this.selectedItems();
        const expectedQuantity = Number(this.activePlan?.dataset.quantity || 1);
        const selectedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        if (!items.length || selectedQuantity !== expectedQuantity) {
          this.showError(aurenText('choose_error'));
          return;
        }

        this.setLoading(true);
        this.showError('');
        (this.querySelector('auren-upsells')?.selectedItems?.() || []).forEach(({ id, quantity }) => {
          const existing = items.find(item => item.id === id);
          if (existing) existing.quantity += quantity;
          else items.push({ id, quantity });
        });
        const cart = document.querySelector('cart-drawer');
        const payload = { items };
        if (cart?.getSectionsToRender) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          cart.setActiveElement?.(this.submitButton);
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || aurenText('add_error'));

          document.dispatchEvent(new CustomEvent('auren:cart-updated'));

          if (cart?.renderContents && payload.sections.every(id => typeof data.sections?.[id] === 'string')) {
            cart.classList.remove('is-empty');
            cart.renderContents(data);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
          this.setStickyVisible(false);
        } catch (error) {
          this.showError(error.message || aurenText('add_error'));
        } finally {
          this.setLoading(false);
          this.update();
        }
      }

      mountStickyBar() {
        // Reveal animations create a containing block for fixed descendants.
        // Keep the mobile purchase bar attached to the viewport, not the product card.
        if (this.sticky) document.body.appendChild(this.sticky);
      }

      setStickyVisible(visible) {
        if (!this.sticky) return;
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.setAttribute('aria-hidden', String(!visible));
        this.sticky.toggleAttribute('inert', !visible);
      }

      initStickyBar() {
        this.setStickyVisible(false);
        if (!this.submitButton || !this.sticky) return;
        // A fast scroll may skip the button without changing its intersection state.
        // One measurement per animation frame also handles jumps and viewport changes.
        this.queueStickyUpdate ||= () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => {
            this.stickyFrame = null;
            const dialogOpen = document.body.classList.contains('auren-size-help-open');
            this.setStickyVisible(window.innerWidth <= 749 && !dialogOpen && this.submitButton.getBoundingClientRect().bottom <= 0);
          });
        };
        window.addEventListener('scroll', this.queueStickyUpdate, { passive: true });
        window.addEventListener('resize', this.queueStickyUpdate, { passive: true });
        this.queueStickyUpdate();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.queueStickyUpdate);
        window.removeEventListener('resize', this.queueStickyUpdate);
        cancelAnimationFrame(this.stickyFrame);
        this.sticky?.remove();
      }
    });
  }
})();
